import Box from '@mui/material/Box';

import { AppButton, Reveal } from '@/components/atoms';
import { SectionHeading } from '@/components/molecules';
import { Section, type SectionTone } from '@/components/organisms';
import { deliveryProofBand as copy, deliveryProofCta } from '@/content/deliveryProof';
import { useDeliveryShowcase } from '@/hooks/useDeliveryShowcase';
import { type SectionPatternName } from '@/theme';

import { DeliveryMediaViewers } from './DeliveryMediaViewers';
import { DeliveryTile } from './DeliveryTile';

type Props = {
  /** Unique per page: two bands on one page would collide on the id. */
  id?: string;
  tone?: SectionTone;
  pattern?: SectionPatternName;
  /** Overridden where the host page's own argument needs a different angle. */
  title?: string;
  lede?: string;
  /** Six fills two rows of three; four fits a page that only has room for one. */
  limit?: number;
};

/**
 * A full band of proof, for a page with a band to spare.
 *
 * ## It removes itself
 *
 * No photographs, no band — not an empty heading, not a row of placeholders.
 * That is what lets this be added to seven pages now, before Kabex has
 * supplied anything: every one of them renders exactly as it does today until
 * the archive has something in it, and then all seven gain a wall of
 * photographs in the same commit that drops the files in.
 *
 * ## Tiles here open the same viewers as the gallery
 *
 * A proof band whose tiles merely linked to the gallery would be a band
 * nobody taps: the cost of a tap is a page load, and the reward is unknown.
 * Tapping opens the picture full screen, which is the thing being offered —
 * and the button underneath is still there for anyone who wants the rest.
 *
 * ## Fixed-ratio tiles, not the gallery's masonry
 *
 * The wall on the gallery page takes each photograph's own shape because it is
 * the page's whole content and has the room to. A band inside another page has
 * a height the page is budgeting for, so these tiles are a uniform 4:3 and the
 * band stays the size it looks like it will be.
 */
export function DeliveryProofBand({
  id = 'delivery-proof-title',
  tone = 'muted',
  pattern = 'grid',
  title = copy.title,
  lede = copy.lede,
  limit = 6,
}: Props) {
  const { items, viewers, empty } = useDeliveryShowcase(limit);

  if (empty) return null;

  return (
    <Section tone={tone} pattern={pattern} labelledBy={id}>
      <SectionHeading id={id} eyebrow={copy.eyebrow} title={title} lede={lede} />

      <Box
        component="ul"
        sx={{
          listStyle: 'none',
          p: 0,
          m: 0,
          display: 'grid',
          gap: 4,
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            lg: `repeat(${Math.min(items.length, 3)}, 1fr)`,
          },
        }}
      >
        {items.map((item, index) => (
          <Box component="li" key={item.media.id} sx={{ minWidth: 0 }}>
            <Reveal delay={(index % 3) * 80} sx={{ height: '100%' }}>
              <DeliveryTile
                item={item}
                onOpen={viewers.openItem}
                sizes="(min-width: 1200px) 368px, (min-width: 600px) 30vw, 50vw"
                sx={{ aspectRatio: '4 / 3' }}
              />
            </Reveal>
          </Box>
        ))}
      </Box>

      <Reveal>
        <AppButton to={deliveryProofCta.to} appVariant="secondary" sx={{ mt: 10 }}>
          {deliveryProofCta.label}
        </AppButton>
      </Reveal>

      <DeliveryMediaViewers viewers={viewers} />
    </Section>
  );
}
